"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

import { GTM_ID, YANDEX_METRIKA_ID } from "@/shared/config/analytics-ids";
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  hasAnalyticsConsent,
} from "@/shared/lib/hooks/use-cookie-consent";

/**
 * Яндекс.Метрика и Google Tag Manager (KAN-1198).
 *
 * Оба ставят куки, поэтому грузятся строго после «Принять» в куки-баннере и
 * ни разу — до него. Umami (cookieless) живёт отдельно, в app/layout.tsx.
 *
 * GA4 в код не вшит: его тег настраивается внутри контейнера GTM. Так новые
 * теги и события добавляются в интерфейсе Google без релиза сайта.
 *
 * Идентификаторы — в shared/config/analytics-ids.ts.
 *
 * SPA-переходы отдельно не отправляем: Метрика (tag.js) и GA4 (enhanced
 * measurement) сами слушают History API. Ручной hit поверх дал бы двойные
 * просмотры.
 */

const METRIKA_SNIPPET = (id: number) => `
(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
m[i].l=1*new Date();
for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
ym(${id}, "init", {clickmap:true, trackLinks:true, accurateTrackBounce:true, webvisor:true});
`;

const GTM_SNIPPET = (id: string) => `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');
`;

export function ThirdPartyAnalytics() {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const sync = () => setAllowed(hasAnalyticsConsent());
    sync();
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, sync);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, sync);
  }, []);

  // Отзыв согласия не выгружает уже загруженный скрипт — это невозможно без
  // перезагрузки страницы. Следующая загрузка пройдёт уже без трекеров.
  if (!allowed || process.env.NODE_ENV === "development") return null;

  return (
    <>
      {YANDEX_METRIKA_ID > 0 && (
        <Script id="yandex-metrika" strategy="afterInteractive">
          {METRIKA_SNIPPET(YANDEX_METRIKA_ID)}
        </Script>
      )}
      {GTM_ID && (
        <Script id="google-tag-manager" strategy="afterInteractive">
          {GTM_SNIPPET(GTM_ID)}
        </Script>
      )}
    </>
  );
}
